import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, X } from 'lucide-react';
import { useAuthContext } from '@/contexts/useAuthContext';
import { buildDebugReport, type DebugReport } from './debugSnapshot';

const TAP_WINDOW_MS = 3000;
const TAP_TARGET = 7;

function isoToFilename(iso: string): string {
  return iso.replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);
}

const DebugReportOverlay: React.FC = () => {
  const auth = useAuthContext();
  const queryClient = useQueryClient();
  const [isVisible, setIsVisible] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<DebugReport | null>(null);
  const tapTimestamps = useRef<number[]>([]);

  const generate = useCallback(async () => {
    setIsGenerating(true);
    setIsVisible(true);
    const result = buildDebugReport(auth, queryClient);
    setReport(result);
    setIsGenerating(false);
  }, [auth, queryClient]);

  const handleFooterTap = useCallback(() => {
    const now = Date.now();
    tapTimestamps.current = tapTimestamps.current.filter((t) => now - t < TAP_WINDOW_MS);
    tapTimestamps.current.push(now);
    if (tapTimestamps.current.length >= TAP_TARGET) {
      tapTimestamps.current = [];
      generate();
    }
  }, [generate]);

  // Register trigger and tap handler on bridge
  useEffect(() => {
    if (!window.__debugBridge) return;
    window.__debugBridge.triggerDebugReport = generate;
    window.__debugBridge.onFooterTap = handleFooterTap;
    return () => {
      if (window.__debugBridge) {
        window.__debugBridge.triggerDebugReport = null;
        window.__debugBridge.onFooterTap = null;
      }
    };
  }, [generate, handleFooterTap]);

  // Keyboard shortcut: Ctrl+Shift+D or Cmd+Shift+D
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        generate();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [generate]);

  const handleDownload = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-report-${isoToFilename(report.meta.generatedAt)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Debug Report</h2>
          <button
            onClick={() => setIsVisible(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isGenerating ? (
          <div className="py-6 text-center text-gray-500 text-sm">
            <div className="inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mb-2" />
            <p>Collecting data...</p>
          </div>
        ) : report ? (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs text-gray-700 space-y-1">
              <div><span className="text-gray-400">time</span> {report.meta.generatedAt}</div>
              <div><span className="text-gray-400">session</span> {report.meta.sessionId ?? 'n/a'}</div>
              <div><span className="text-gray-400">env</span> {report.meta.environment} {report.meta.appVersion !== 'unknown' ? `v${report.meta.appVersion}` : ''}</div>
              <div><span className="text-gray-400">errors</span> {report.console.filter((e) => e.level === 'error').length} console, {report.network.filter((n) => n.error).length} network</div>
              <div>
                <span className="text-gray-400">form</span>{' '}
                {report.form.activeForm
                  ? `${report.form.activeForm} step ${(report.form.currentStep ?? 0) + 1}/${report.form.stepCount}`
                  : 'none open'}
              </div>
              <div><span className="text-gray-400">page</span> {report.page.name ?? 'n/a'}</div>
              <div><span className="text-gray-400">auth</span> {report.auth.isAuthenticated ? `${report.auth.emailPartial} (${report.auth.role})` : 'not signed in'}</div>
            </div>

            <div className="flex gap-2">
              <button onClick={handleDownload} className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm">
                <Download className="w-4 h-4" />
                Download Report
              </button>
              <button onClick={() => setIsVisible(false)} className="btn-outline px-4 text-sm">
                Close
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DebugReportOverlay;
